import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { fmt } from '@dcc-erp/shared';
import type {
  TaxReconForm as ApiTaxReconForm,
  TaxReconRow as ApiTaxReconRow,
  TaxReconSnapshotResponse,
  TaxReconStatus,
} from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { PageHead } from '../components/layout';
import { useActiveCompanyId } from '../lib/queries/companies';
import {
  useRefreshTaxRecon,
  useTaxReconSnapshot,
  useUpdateTaxRecon,
} from '../lib/queries/tax-recon';

type FormCode = 'PND1' | 'PND3' | 'PND53' | 'PND54';
type TabKey = 'dashboard' | 'pnd1' | 'pnd3' | 'pnd53' | 'pnd54' | 'variance';
type VarianceType = 'cert_no_gl' | 'gl_no_cert' | 'amount_mismatch';
type VarianceSeverity = 'critical' | 'warning';
type ReconStatus = 'filed' | 'preparing';

interface TaxForm {
  code: FormCode;
  label: string;
  desc: string;
  acctExp: string;
  acctLiab: string;
  due: string;
}

interface FormSnapshot {
  base: number;
  wht: number;
}

interface CertSnapshot extends FormSnapshot {
  count: number;
}

interface FiledSnapshot extends FormSnapshot {
  refNo: string;
}

interface ReconData {
  period: string;
  fromGL: FormSnapshot;
  fromCert: CertSnapshot;
  filed: FiledSnapshot | null;
  status: ReconStatus;
}

interface Variance {
  id: string;
  form: FormCode;
  type: VarianceType;
  vendor: string;
  certAmt: number;
  glAmt: number;
  severity: VarianceSeverity;
  reason: string;
  suggestion: string;
}

interface DetailRow {
  vendor: string;
  tax: string;
  type: string;
  base: number;
  gl: number;
  cert: number;
  diff?: number;
}

interface FilingDeadline {
  date: string;
  label: string;
  days: number;
}

interface AnnualMonthRow {
  month: string;
  count: number;
  base: number;
  wht: number;
}

const TR_FORMS: readonly TaxForm[] = [
  {
    code: 'PND1',
    label: 'ภ.ง.ด.1',
    desc: 'พนักงาน (เงินเดือน)',
    acctExp: '6410',
    acctLiab: '2131-PR',
    due: 'วันที่ 7 ของเดือนถัดไป',
  },
  {
    code: 'PND3',
    label: 'ภ.ง.ด.3',
    desc: 'บุคคลธรรมดา',
    acctExp: 'หลายบัญชี',
    acctLiab: '2131-3',
    due: 'วันที่ 7 ของเดือนถัดไป',
  },
  {
    code: 'PND53',
    label: 'ภ.ง.ด.53',
    desc: 'นิติบุคคล',
    acctExp: 'หลายบัญชี',
    acctLiab: '2131-53',
    due: 'วันที่ 7 ของเดือนถัดไป',
  },
  {
    code: 'PND54',
    label: 'ภ.ง.ด.54',
    desc: 'จ่ายต่างประเทศ',
    acctExp: 'หลายบัญชี',
    acctLiab: '2131-54',
    due: 'วันที่ 7 ของเดือนถัดไป',
  },
];

// Empty default for each form code. Snapshot rows from the API overlay these
// shells when present; otherwise the table renders zero/empty-state rows.
function emptyReconData(displayPeriod: string): ReconData {
  return {
    period: displayPeriod,
    fromGL: { base: 0, wht: 0 },
    fromCert: { base: 0, wht: 0, count: 0 },
    filed: null,
    status: 'preparing',
  };
}

const FILING_DEADLINES: readonly FilingDeadline[] = [
  { date: '2026-06-07', label: 'ยื่น ภ.ง.ด.1 / 3 / 53 / 54 งวด พ.ค.', days: 13 },
  { date: '2026-06-15', label: 'ยื่น ภ.พ.30 งวด พ.ค.', days: 21 },
  { date: '2027-02-28', label: 'ยื่น ภ.ง.ด.1ก รายปี 2026', days: 280 },
  { date: '2027-05-31', label: 'ยื่น ภ.ง.ด.50 ภาษีนิติบุคคลรายปี 2026', days: 372 },
];

const TH_MONTHS: readonly string[] = [
  'ม.ค.',
  'ก.พ.',
  'มี.ค.',
  'เม.ย.',
  'พ.ค.',
  'มิ.ย.',
  'ก.ค.',
  'ส.ค.',
  'ก.ย.',
  'ต.ค.',
  'พ.ย.',
  'ธ.ค.',
];

// Per-form detail rows are not yet persisted — render empty state until the
// API exposes vendor-level breakdowns.
const EMPTY_DETAIL_ROWS: readonly DetailRow[] = [];

// Annual roll-up modal — empty until backend exposes a monthly aggregate.
const ANNUAL_MONTHLY: readonly AnnualMonthRow[] = [];

export const Route = createFileRoute('/taxrecon')({ component: TaxReconPage });

// Map between the period code in the API ('YYYYMM') and the period dropdown value.
const PERIOD_OPTIONS: readonly { value: string; label: string; displayPeriod: string }[] = [
  { value: '202605', label: 'งวด พ.ค. 2026', displayPeriod: '05/2026' },
  { value: '202604', label: 'งวด เม.ย. 2026', displayPeriod: '04/2026' },
  { value: '202603', label: 'งวด มี.ค. 2026', displayPeriod: '03/2026' },
];

// API form codes (PHO_PHO_30 = ภ.พ.30, excluded from per-form tabs).
const API_FORM_FOR_CODE: Readonly<Record<FormCode, ApiTaxReconForm>> = {
  PND1: 'PND1',
  PND3: 'PND3',
  PND53: 'PND53',
  PND54: 'PND54',
};

function TaxReconPage() {
  const companyId = useActiveCompanyId();
  const [periodValue, setPeriodValue] = useState<string>(PERIOD_OPTIONS[0]?.value ?? '202605');
  const [tab, setTab] = useState<TabKey>('dashboard');
  const [showRun, setShowRun] = useState(false);
  const [showVar, setShowVar] = useState<Variance | null>(null);
  const [showAnnual, setShowAnnual] = useState(false);

  const snapshotQ = useTaxReconSnapshot(companyId, periodValue);
  const refreshM = useRefreshTaxRecon(companyId);

  // Merge live API rows into the prototype's TR_DATA shape — preserves the
  // prototype DOM while showing real GL aggregation. Variances stay client-side
  // (3-way recon vs WhtCertificate isn't modeled in phase-3 yet).
  const apiByForm = useMemo(() => {
    const m = new Map<ApiTaxReconForm, ApiTaxReconRow>();
    snapshotQ.data?.rows.forEach((r) => m.set(r.taxForm, r));
    return m;
  }, [snapshotQ.data]);

  const trData = useMemo<Readonly<Record<FormCode, ReconData>>>(() => {
    const displayPeriod =
      PERIOD_OPTIONS.find((p) => p.value === periodValue)?.displayPeriod ?? '05/2026';
    const out: Record<FormCode, ReconData> = {
      PND1: emptyReconData(displayPeriod),
      PND3: emptyReconData(displayPeriod),
      PND53: emptyReconData(displayPeriod),
      PND54: emptyReconData(displayPeriod),
    };
    (Object.keys(out) as FormCode[]).forEach((code) => {
      const apiRow = apiByForm.get(API_FORM_FOR_CODE[code]);
      if (!apiRow) return;
      const gl = Number(apiRow.glAmount);
      const filed = Number(apiRow.filedAmount);
      out[code] = {
        period: displayPeriod,
        fromGL: { base: 0, wht: gl },
        fromCert: { base: 0, wht: gl, count: 0 },
        filed:
          apiRow.filingDataAvailable && filed > 0
            ? { base: 0, wht: filed, refNo: `${code}-${periodValue}` }
            : null,
        status: filed > 0 ? 'filed' : 'preparing',
      };
    });
    return out;
  }, [apiByForm, periodValue]);

  // Live variances derived from API rows whose status != RESOLVED and severity != OK.
  // Empty when nothing has been flagged.
  const variances = useMemo<readonly Variance[]>(() => {
    if (!snapshotQ.data) return [];
    const apiVariances: Variance[] = snapshotQ.data.rows
      .filter((r) => r.taxForm !== 'PHO_PHO_30' && r.status !== 'RESOLVED' && r.severity !== 'OK')
      .map((r) => {
        const gl = Number(r.glAmount);
        const filed = Number(r.filedAmount);
        const diff = gl - filed;
        const code = r.taxForm as FormCode;
        const type: VarianceType =
          filed === 0 && gl > 0
            ? 'gl_no_cert'
            : gl === 0 && filed > 0
              ? 'cert_no_gl'
              : 'amount_mismatch';
        return {
          id: r.id,
          form: code,
          type,
          vendor: `${code} variance · งวด ${r.period}`,
          certAmt: filed,
          glAmt: gl,
          severity: r.severity === 'CRITICAL' ? 'critical' : 'warning',
          reason: r.explanation ?? `GL − Filed = ${diff.toFixed(2)} (${r.variancePercent}%)`,
          suggestion:
            type === 'cert_no_gl'
              ? 'Post Adjusting JV (catch-up expense + Cr 2131)'
              : type === 'gl_no_cert'
                ? 'ออก Cert ย้อนหลัง · ปรับ Filing record'
                : 'ปรับ JV หรือยอด Filing ให้ตรง',
        };
      });
    return apiVariances;
  }, [snapshotQ.data]);

  const passedCount = TR_FORMS.filter((f) => !variances.some((v) => v.form === f.code)).length;
  const criticalCount = variances.filter((v) => v.severity === 'critical').length;
  const total2131 = useMemo(() => {
    if (!snapshotQ.data) return 0;
    return snapshotQ.data.rows
      .filter((r) => r.taxForm !== 'PHO_PHO_30')
      .reduce((s, r) => s + Number(r.glAmount), 0);
  }, [snapshotQ.data]);

  const tabs: readonly (readonly [TabKey, string])[] = [
    ['dashboard', 'Dashboard'],
    ['pnd1', 'ภ.ง.ด.1 (Payroll)'],
    ['pnd3', 'ภ.ง.ด.3 (บุคคล)'],
    ['pnd53', 'ภ.ง.ด.53 (นิติบุคคล)'],
    ['pnd54', 'ภ.ง.ด.54 (ต่างประเทศ)'],
    ['variance', `Variance (${variances.length})`],
  ];

  return (
    <div className="page">
      <PageHead
        crumbs={['รายงานและภาษี', 'กระทบยอดภาษีหัก ณ ที่จ่าย']}
        title="กระทบยอดภาษีหัก ณ ที่จ่าย — GL ↔ Cert ↔ ภ.ง.ด."
        actions={
          <>
            <select
              className="select"
              value={periodValue}
              onChange={(e) => setPeriodValue(e.target.value)}
            >
              {PERIOD_OPTIONS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
            <button className="btn" onClick={() => setShowAnnual(true)}>
              <Icon name="reports" size={14} /> ภ.ง.ด.1ก รายปี
            </button>
            <button className="btn primary" onClick={() => setShowRun(true)}>
              <Icon name="refresh" size={14} /> Run Reconciliation
            </button>
          </>
        }
      />

      <div className="kpi-grid">
        <div className="kpi">
          <div className="label">
            <span className="dot" />
            ยอด 2131 ภาษีค้างนำส่ง
          </div>
          <div className="value tnum">
            {fmt(total2131)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">ก่อนยื่น 7 มิ.ย. 2026</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--positive)' }} />
            กระทบยอดผ่าน
          </div>
          <div className="value tnum" style={{ color: 'var(--positive)' }}>
            {passedCount} <span className="unit">/ {TR_FORMS.length} แบบ</span>
          </div>
          <div className="trend pos">
            <Icon name="check" size={11} /> PND1 / PND53 / PND54
          </div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--warning)' }} />
            Variance Items
          </div>
          <div className="value tnum">
            {variances.length} <span className="unit">รายการ</span>
          </div>
          <div className="trend down">{criticalCount} critical</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--negative)' }} />
            เหลือเวลา
          </div>
          <div className="value tnum" style={{ color: 'var(--brand)' }}>
            13 <span className="unit">วัน</span>
          </div>
          <div className="trend down">ถึง 7 มิ.ย. 2026</div>
        </div>
      </div>

      <div
        style={{
          background: '#fafafa',
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: '14px 18px',
          marginBottom: 'var(--section-gap)',
        }}
      >
        <div className="section-title" style={{ margin: '0 0 10px' }}>
          3-Way Reconciliation — ทั้ง 3 ตัวเลขต้องตรงกัน
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto 1fr auto 1fr',
            gap: 14,
            alignItems: 'center',
          }}
        >
          <div
            style={{
              padding: '14px',
              background: 'white',
              borderRadius: 8,
              border: '2px solid #1a56a6',
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: 20, marginBottom: 4 }}>📊</div>
            <div style={{ fontWeight: 700, color: '#1a56a6' }}>GL Movement</div>
            <div className="meta" style={{ marginTop: 4 }}>
              รายการในบัญชี 2131 + 5xxx
            </div>
          </div>
          <div style={{ fontSize: 24, color: 'var(--text-faint)', fontWeight: 700 }}>↔</div>
          <div
            style={{
              padding: '14px',
              background: 'white',
              borderRadius: 8,
              border: '2px solid #117a3d',
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: 20, marginBottom: 4 }}>📄</div>
            <div style={{ fontWeight: 700, color: '#117a3d' }}>WHT Certificates</div>
            <div className="meta" style={{ marginTop: 4 }}>
              หนังสือ 50 ทวิ ที่ออก
            </div>
          </div>
          <div style={{ fontSize: 24, color: 'var(--text-faint)', fontWeight: 700 }}>↔</div>
          <div
            style={{
              padding: '14px',
              background: 'white',
              borderRadius: 8,
              border: '2px solid #b54708',
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: 20, marginBottom: 4 }}>📋</div>
            <div style={{ fontWeight: 700, color: '#b54708' }}>ที่ยื่นในแบบ ภ.ง.ด.</div>
            <div className="meta" style={{ marginTop: 4 }}>
              e-Filing สรรพากร
            </div>
          </div>
        </div>
      </div>

      <div className="page-tabs">
        {tabs.map(([k, l]) => (
          <div key={k} className={`pt ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>
            {l}
          </div>
        ))}
      </div>

      {tab === 'dashboard' && (
        <TRDashboard onVar={setShowVar} trData={trData} variances={variances} />
      )}
      {tab === 'pnd1' && <TRForm code="PND1" trData={trData} variances={variances} />}
      {tab === 'pnd3' && <TRForm code="PND3" trData={trData} variances={variances} />}
      {tab === 'pnd53' && <TRForm code="PND53" trData={trData} variances={variances} />}
      {tab === 'pnd54' && <TRForm code="PND54" trData={trData} variances={variances} />}
      {tab === 'variance' && <TRVariance onVar={setShowVar} variances={variances} />}

      {showRun && (
        <RunReconModal
          period={periodValue}
          isRefreshing={refreshM.isPending}
          onRun={() => {
            refreshM.mutate({ period: periodValue }, { onSettled: () => setShowRun(false) });
          }}
          onClose={() => setShowRun(false)}
        />
      )}
      {showVar && (
        <VarianceModal
          v={showVar}
          companyId={companyId}
          snapshot={snapshotQ.data}
          onClose={() => setShowVar(null)}
        />
      )}
      {showAnnual && <AnnualModal onClose={() => setShowAnnual(false)} />}
    </div>
  );
}

function TRDashboard({
  onVar,
  trData,
  variances,
}: {
  onVar: (v: Variance) => void;
  trData: Readonly<Record<FormCode, ReconData>>;
  variances: readonly Variance[];
}) {
  return (
    <>
      <div className="tbl-shell" style={{ marginBottom: 'var(--section-gap)' }}>
        <div className="filter-row">
          <span style={{ fontSize: 14, fontWeight: 600 }}>
            3-Way Reconciliation Summary · ทุกแบบ ภ.ง.ด.
          </span>
        </div>
        <table className="tbl">
          <thead>
            <tr>
              <th>แบบ ภ.ง.ด.</th>
              <th>งวด</th>
              <th className="num">GL Movement</th>
              <th className="num">WHT Cert</th>
              <th className="num">ที่ยื่นในแบบ</th>
              <th className="num">Variance</th>
              <th>สถานะกระทบยอด</th>
              <th>การยื่น</th>
              <th>การกระทำ</th>
            </tr>
          </thead>
          <tbody>
            {TR_FORMS.map((f) => {
              const d = trData[f.code];
              const filedAmt = d.filed ? d.filed.wht : null;
              const formVariances = variances.filter((v) => v.form === f.code);
              const hasVariance = formVariances.length > 0;
              const varianceSum = formVariances.reduce((s, v) => s + (v.certAmt - v.glAmt), 0);
              const firstVar = formVariances[0];
              return (
                <tr key={f.code}>
                  <td>
                    <b>{f.label}</b>
                    <div className="meta">{f.desc}</div>
                  </td>
                  <td>
                    <b>{d.period}</b>
                  </td>
                  <td className="num tnum">{fmt(d.fromGL.wht)}</td>
                  <td className="num tnum">{fmt(d.fromCert.wht)}</td>
                  <td className="num tnum">
                    {filedAmt !== null ? (
                      fmt(filedAmt)
                    ) : (
                      <span className="faint">— ยังไม่ยื่น —</span>
                    )}
                  </td>
                  <td className="num tnum">
                    {hasVariance ? (
                      <span style={{ color: 'var(--negative)', fontWeight: 600 }}>
                        {fmt(varianceSum)}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--positive)', fontWeight: 600 }}>0 ✓</span>
                    )}
                  </td>
                  <td>
                    {hasVariance ? (
                      <span className="badge negative">
                        <span className="bdot" />
                        มี Variance
                      </span>
                    ) : (
                      <span className="badge positive">
                        <Icon name="check" size={10} />
                        ตรงกัน
                      </span>
                    )}
                  </td>
                  <td>
                    {d.status === 'filed' ? (
                      <span className="badge positive">
                        <Icon name="check" size={10} />
                        ยื่นแล้ว
                      </span>
                    ) : (
                      <span className="badge warning">
                        <span className="bdot" />
                        เตรียมยื่น
                      </span>
                    )}
                  </td>
                  <td>
                    {hasVariance && firstVar ? (
                      <button
                        className="btn sm primary"
                        style={{ height: 28, fontSize: 14 }}
                        onClick={() => onVar(firstVar)}
                      >
                        แก้ Variance →
                      </button>
                    ) : d.status === 'filed' ? (
                      <button
                        className="btn sm ghost"
                        style={{ height: 28, fontSize: 14 }}
                        onClick={() => {
                          /* TODO(phase-3): wire */
                        }}
                      >
                        ดูแบบที่ยื่น
                      </button>
                    ) : (
                      <button
                        className="btn sm"
                        style={{ height: 28, fontSize: 14 }}
                        onClick={() => {
                          /* TODO(phase-3): wire */
                        }}
                      >
                        ดู Detail
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {TR_FORMS.length > 0 &&
              (() => {
                const sumGL = TR_FORMS.reduce((s, f) => s + trData[f.code].fromGL.wht, 0);
                const sumCert = TR_FORMS.reduce((s, f) => s + trData[f.code].fromCert.wht, 0);
                const sumFiled = TR_FORMS.reduce((s, f) => s + (trData[f.code].filed?.wht ?? 0), 0);
                const sumVar = variances.reduce((s, v) => s + (v.certAmt - v.glAmt), 0);
                return (
                  <tr className="total">
                    <td colSpan={2}>รวมทุกแบบ</td>
                    <td className="num tnum">{fmt(sumGL)}</td>
                    <td className="num tnum">{fmt(sumCert)}</td>
                    <td className="num tnum">{fmt(sumFiled)}</td>
                    <td className="num tnum" style={{ color: 'var(--negative)' }}>
                      {fmt(sumVar)}
                    </td>
                    <td colSpan={3} />
                  </tr>
                );
              })()}
          </tbody>
        </table>
      </div>

      <div className="grid-2">
        <div className="card">
          <div className="card-head">
            <h3>Account 2131 — Movement</h3>
          </div>
          <div className="card-body flush">
            <table className="tbl">
              <tbody>
                <tr>
                  <td colSpan={2} className="muted" style={{ padding: 32, textAlign: 'center' }}>
                    ยังไม่มีข้อมูลสำหรับช่วงเวลานี้
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h3>กำหนดเวลายื่น</h3>
          </div>
          <div className="card-body">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {FILING_DEADLINES.map((d) => {
                const monthIdx = Number.parseInt(d.date.slice(5, 7), 10) - 1;
                const day = Number.parseInt(d.date.slice(8), 10);
                const urgent = d.days <= 14;
                return (
                  <div
                    key={d.date}
                    style={{
                      display: 'flex',
                      gap: 14,
                      padding: '10px 14px',
                      background: urgent ? '#fef3e8' : '#fafafa',
                      borderRadius: 6,
                      border: `1px solid ${urgent ? '#f0dab0' : 'var(--border)'}`,
                    }}
                  >
                    <div style={{ width: 48, textAlign: 'center' }}>
                      <div style={{ fontSize: 11, color: 'var(--text-faint)' }}>
                        {TH_MONTHS[monthIdx] ?? ''}
                      </div>
                      <div
                        style={{
                          fontSize: 22,
                          fontWeight: 700,
                          fontFamily: 'var(--font-display)',
                        }}
                      >
                        {day}
                      </div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>{d.label}</div>
                      <div className="meta">
                        เหลือ <b>{d.days} วัน</b>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function TRForm({
  code,
  trData,
  variances,
}: {
  code: FormCode;
  trData: Readonly<Record<FormCode, ReconData>>;
  variances: readonly Variance[];
}) {
  const f = TR_FORMS.find((x) => x.code === code);
  const d = trData[code];
  const formVariances = variances.filter((v) => v.form === code);
  if (!f) return null;

  const isPND1 = code === 'PND1';
  const rows: readonly DetailRow[] = EMPTY_DETAIL_ROWS;

  const filedMatch = d.filed ? d.fromCert.wht === d.filed.wht : true;
  const glCertMatch = d.fromGL.wht === d.fromCert.wht;
  const variancesNetGL = formVariances.reduce((s, v) => s + (v.glAmt - v.certAmt), 0);

  return (
    <>
      <div className="card" style={{ marginBottom: 'var(--section-gap)' }}>
        <div className="card-head">
          <div>
            <h3>
              {f.label} — {f.desc} · งวด {d.period}
            </h3>
            <div className="sub">
              บัญชีค่าใช้จ่าย: {f.acctExp} · บัญชีภาษีค้าง:{' '}
              <span className="code">{f.acctLiab}</span> · กำหนดยื่น: {f.due}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              className="btn sm"
              onClick={() => {
                /* TODO(phase-3): wire */
              }}
            >
              <Icon name="download" size={12} /> Export
            </button>
            <button
              className="btn sm primary"
              onClick={() => {
                /* TODO(phase-3): wire */
              }}
            >
              <Icon name="upload" size={12} /> ยื่น e-Filing
            </button>
          </div>
        </div>
        <div style={{ padding: '18px', background: '#fafafa' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr auto 1fr auto 1fr auto 1fr',
              gap: 14,
              alignItems: 'center',
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <div className="meta">📊 GL Movement</div>
              <div
                style={{
                  fontSize: 24,
                  fontWeight: 700,
                  marginTop: 4,
                  color: '#1a56a6',
                  fontFamily: 'var(--font-display)',
                }}
                className="tnum"
              >
                {fmt(d.fromGL.wht)}
              </div>
              <div className="meta">ฐาน {fmt(d.fromGL.base)}</div>
            </div>
            <div
              style={{
                fontSize: 24,
                color: glCertMatch ? 'var(--positive)' : 'var(--negative)',
                fontWeight: 700,
              }}
            >
              {glCertMatch ? '=' : '≠'}
            </div>
            <div style={{ textAlign: 'center' }}>
              <div className="meta">📄 {isPND1 ? 'HRM Payroll' : 'WHT Certificates'}</div>
              <div
                style={{
                  fontSize: 24,
                  fontWeight: 700,
                  marginTop: 4,
                  color: '#117a3d',
                  fontFamily: 'var(--font-display)',
                }}
                className="tnum"
              >
                {fmt(d.fromCert.wht)}
              </div>
              <div className="meta">
                {d.fromCert.count} {isPND1 ? 'พนักงาน' : 'รายการ'}
              </div>
            </div>
            <div
              style={{
                fontSize: 24,
                color: filedMatch ? 'var(--positive)' : 'var(--negative)',
                fontWeight: 700,
              }}
            >
              {d.filed ? '=' : '→'}
            </div>
            <div style={{ textAlign: 'center' }}>
              <div className="meta">📋 ที่ยื่น/จะยื่น</div>
              <div
                style={{
                  fontSize: 24,
                  fontWeight: 700,
                  marginTop: 4,
                  color: '#b54708',
                  fontFamily: 'var(--font-display)',
                }}
                className="tnum"
              >
                {d.filed ? fmt(d.filed.wht) : 'รอยื่น'}
              </div>
              <div className="meta">{d.filed ? d.filed.refNo : 'ก่อน 7 มิ.ย. 2026'}</div>
            </div>
            <div
              style={{
                fontSize: 24,
                color: formVariances.length === 0 ? 'var(--positive)' : 'var(--negative)',
                fontWeight: 700,
              }}
            >
              {formVariances.length === 0 ? '✓' : '!'}
            </div>
            <div style={{ textAlign: 'center' }}>
              <div className="meta">Status</div>
              <div style={{ fontSize: 14, fontWeight: 600, marginTop: 6 }}>
                {formVariances.length === 0 ? (
                  <span className="badge positive">ตรงทั้ง 3 ฝั่ง</span>
                ) : (
                  <span className="badge negative">{formVariances.length} Variance</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="tbl-shell">
        <div className="filter-row">
          <span style={{ fontSize: 14, fontWeight: 600 }}>รายละเอียดเปรียบเทียบ 3 ฝั่ง</span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <button
              className="btn sm"
              onClick={() => {
                /* TODO(phase-3): wire */
              }}
            >
              Export Excel
            </button>
          </div>
        </div>
        <table className="tbl">
          <thead>
            <tr>
              <th>{isPND1 ? 'พนักงาน' : 'Vendor / ผู้รับเงิน'}</th>
              <th>เลขประจำตัว</th>
              <th>ประเภทเงินได้</th>
              <th className="num">ฐาน (GL)</th>
              <th className="num">WHT (GL)</th>
              <th className="num">WHT (Cert/HRM)</th>
              <th className="num">Diff</th>
              <th>สถานะ</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={8} className="muted" style={{ padding: 32, textAlign: 'center' }}>
                  ยังไม่มีข้อมูลสำหรับช่วงเวลานี้
                </td>
              </tr>
            )}
            {rows.map((r) => {
              const diff = r.diff ?? 0;
              const hasDiff = diff !== 0;
              return (
                <tr key={r.vendor} style={hasDiff ? { background: '#fef3e8' } : undefined}>
                  <td>
                    <b>{r.vendor}</b>
                  </td>
                  <td className="code muted">{r.tax}</td>
                  <td>{r.type}</td>
                  <td className="num tnum">{fmt(r.base)}</td>
                  <td className="num tnum">{fmt(r.gl)}</td>
                  <td className="num tnum">{fmt(r.cert)}</td>
                  <td className="num tnum">
                    {hasDiff ? (
                      <span style={{ color: 'var(--negative)', fontWeight: 600 }}>
                        {diff > 0 ? '+' : ''}
                        {fmt(diff)}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--positive)', fontWeight: 600 }}>0 ✓</span>
                    )}
                  </td>
                  <td>
                    {hasDiff ? (
                      code === 'PND3' ? (
                        <span className="badge warning">GL no Cert</span>
                      ) : (
                        <span className="badge negative">Variance</span>
                      )
                    ) : (
                      <span className="badge positive">ตรงกัน</span>
                    )}
                  </td>
                </tr>
              );
            })}
            <tr className="total">
              <td colSpan={3}>รวม</td>
              <td className="num tnum">{fmt(d.fromGL.base)}</td>
              <td className="num tnum">{fmt(d.fromGL.wht)}</td>
              <td className="num tnum">{fmt(d.fromCert.wht)}</td>
              <td className="num tnum">
                {formVariances.length > 0 ? (
                  <span style={{ color: 'var(--negative)' }}>{fmt(variancesNetGL)}</span>
                ) : (
                  '0'
                )}
              </td>
              <td />
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
}

function TRVariance({
  onVar,
  variances,
}: {
  onVar: (v: Variance) => void;
  variances: readonly Variance[];
}) {
  const typeMap: Readonly<Record<VarianceType, { label: string; c: 'negative' | 'warning' }>> = {
    cert_no_gl: { label: 'มี Cert · ไม่มี GL', c: 'negative' },
    gl_no_cert: { label: 'มี GL · ไม่มี Cert', c: 'warning' },
    amount_mismatch: { label: 'ยอดไม่ตรง', c: 'warning' },
  };

  return (
    <div className="tbl-shell">
      <div className="filter-row">
        <span style={{ fontSize: 14, fontWeight: 600 }}>Variance ที่ต้องแก้ไขก่อนยื่นแบบ</span>
      </div>
      <table className="tbl">
        <thead>
          <tr>
            <th>เลขที่</th>
            <th>แบบ</th>
            <th>ประเภทปัญหา</th>
            <th>Vendor/พนักงาน</th>
            <th className="num">Cert</th>
            <th className="num">GL</th>
            <th className="num">Diff</th>
            <th>เหตุผล</th>
            <th>ความรุนแรง</th>
            <th>การกระทำ</th>
          </tr>
        </thead>
        <tbody>
          {variances.length === 0 && (
            <tr>
              <td colSpan={10} className="muted" style={{ padding: 32, textAlign: 'center' }}>
                ยังไม่มี variance ที่ persist
              </td>
            </tr>
          )}
          {variances.map((v) => {
            const t = typeMap[v.type];
            const diff = v.certAmt - v.glAmt;
            return (
              <tr key={v.id}>
                <td className="code">
                  <b>{v.id}</b>
                </td>
                <td>
                  <b>{v.form}</b>
                </td>
                <td>
                  <span className={`badge ${t.c}`}>{t.label}</span>
                </td>
                <td>{v.vendor}</td>
                <td className="num tnum">{fmt(v.certAmt)}</td>
                <td className="num tnum">{fmt(v.glAmt)}</td>
                <td className="num tnum">
                  <span style={{ color: 'var(--negative)', fontWeight: 600 }}>
                    {diff > 0 ? '+' : ''}
                    {fmt(diff)}
                  </span>
                </td>
                <td>{v.reason}</td>
                <td>
                  {v.severity === 'critical' ? (
                    <span className="badge negative">Critical</span>
                  ) : (
                    <span className="badge warning">Warning</span>
                  )}
                </td>
                <td>
                  <button
                    className="btn sm primary"
                    style={{ height: 28, fontSize: 14 }}
                    onClick={() => onVar(v)}
                  >
                    แก้ไข →
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function RunReconModal({
  period,
  isRefreshing,
  onRun,
  onClose,
}: {
  period: string;
  isRefreshing: boolean;
  onRun: () => void;
  onClose: () => void;
}) {
  const label = PERIOD_OPTIONS.find((p) => p.value === period)?.label ?? period;
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 720,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <h3
            style={{ margin: 0, fontSize: 18, fontWeight: 600, fontFamily: 'var(--font-display)' }}
          >
            Run Tax Reconciliation
          </h3>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div style={{ padding: '22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label
              style={{
                fontSize: 14,
                color: 'var(--text-muted)',
                fontWeight: 500,
                display: 'block',
                marginBottom: 6,
              }}
            >
              งวด
            </label>
            <select
              className="select"
              style={{ width: '100%', height: 38, fontSize: 14 }}
              value={period}
              disabled
            >
              <option>{label}</option>
            </select>
          </div>
          <div className="section-title" style={{ margin: '8px 0 4px' }}>
            แบบที่จะกระทบยอด
          </div>
          {TR_FORMS.map((f) => (
            <label
              key={f.code}
              style={{
                display: 'flex',
                gap: 10,
                alignItems: 'center',
                padding: '10px 12px',
                background: '#fafafa',
                borderRadius: 6,
                cursor: 'pointer',
              }}
            >
              <input type="checkbox" defaultChecked />
              <div style={{ flex: 1 }}>
                <b>{f.label}</b> — {f.desc}
                <div className="meta">เปรียบเทียบ GL ({f.acctLiab}) ↔ Cert ↔ ที่ยื่น</div>
              </div>
            </label>
          ))}
          <div
            style={{
              padding: '12px 14px',
              background: '#e8eff8',
              borderRadius: 6,
              fontSize: 14,
              lineHeight: 1.55,
              display: 'flex',
              gap: 10,
            }}
          >
            <div
              style={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                background: 'var(--info)',
                color: 'white',
                display: 'grid',
                placeItems: 'center',
                fontSize: 14,
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              i
            </div>
            <div>
              ระบบจะ Sum รายการในบัญชี 2131 + WHT Cert + ที่ยื่นในแบบ · พบ Variance → แสดงพร้อม
              Adjusting JV suggestion
            </div>
          </div>
        </div>
        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose} disabled={isRefreshing}>
            ยกเลิก
          </button>
          <button className="btn primary" onClick={onRun} disabled={isRefreshing}>
            <Icon name="refresh" size={14} /> {isRefreshing ? 'กำลังคำนวณ…' : 'Run Reconciliation'}
          </button>
        </div>
      </div>
    </div>
  );
}

function VarianceModal({
  v,
  companyId,
  snapshot,
  onClose,
}: {
  v: Variance;
  companyId: string | undefined;
  snapshot: TaxReconSnapshotResponse | undefined;
  onClose: () => void;
}) {
  const diff = v.certAmt - v.glAmt;
  const updateM = useUpdateTaxRecon(companyId);
  const [explanation, setExplanation] = useState<string>('');
  // Match the API row by id (live variances use the TaxReconRow.id as their id).
  const apiRow = snapshot?.rows.find((r) => r.id === v.id);
  const canPersist = !!apiRow;
  const persist = (status: TaxReconStatus) => {
    if (!apiRow) {
      onClose();
      return;
    }
    updateM.mutate(
      { id: apiRow.id, data: { status, explanation: explanation || null } },
      { onSettled: onClose },
    );
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 780,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 600,
                fontFamily: 'var(--font-display)',
              }}
            >
              แก้ไข Variance — {v.id}
            </h3>
            <div className="meta" style={{ marginTop: 2 }}>
              {v.form} · {v.vendor}
            </div>
          </div>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div style={{ padding: '22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div
            style={{
              padding: '14px 16px',
              background: v.severity === 'critical' ? '#fce9e7' : '#fef3e8',
              borderRadius: 8,
              border: `1px solid ${v.severity === 'critical' ? '#f4c1bd' : '#f0dab0'}`,
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 4 }}>
              {v.severity === 'critical' ? '⛔ Critical' : '⚠ Warning'}: {v.reason}
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr',
                gap: 14,
                marginTop: 10,
                fontSize: 14,
              }}
            >
              <div>
                <div className="meta">Cert</div>
                <b className="tnum">{fmt(v.certAmt)}</b>
              </div>
              <div>
                <div className="meta">GL</div>
                <b className="tnum">{fmt(v.glAmt)}</b>
              </div>
              <div>
                <div className="meta">Diff</div>
                <b className="tnum" style={{ color: 'var(--negative)' }}>
                  {fmt(diff)}
                </b>
              </div>
            </div>
          </div>

          <div>
            <div className="section-title" style={{ margin: '0 0 8px' }}>
              คำแนะนำการแก้ไข
            </div>
            <div
              style={{
                padding: '12px 14px',
                background: '#e8eff8',
                borderRadius: 6,
                fontSize: 14,
                lineHeight: 1.55,
              }}
            >
              💡 {v.suggestion}
            </div>
          </div>

          {canPersist && (
            <div>
              <div className="section-title" style={{ margin: '0 0 8px' }}>
                บันทึกคำอธิบาย
              </div>
              <textarea
                className="input"
                style={{ width: '100%', minHeight: 80, fontSize: 14, padding: 10 }}
                placeholder="อธิบายสาเหตุ + แนวทางแก้ไข…"
                value={explanation}
                onChange={(e) => setExplanation(e.target.value)}
              />
            </div>
          )}

          <div>
            <div className="section-title" style={{ margin: '0 0 8px' }}>
              Adjusting JV ที่จะสร้าง (Preview)
            </div>
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
              <table className="tbl">
                <thead>
                  <tr>
                    <th>บัญชี</th>
                    <th>ชื่อ</th>
                    <th className="num">Dr</th>
                    <th className="num">Cr</th>
                  </tr>
                </thead>
                <tbody>
                  {v.type === 'cert_no_gl' ? (
                    <>
                      <tr>
                        <td className="code">5210</td>
                        <td>ค่าโฆษณา (Catch-up)</td>
                        <td className="num tnum">{fmt(940_000)}</td>
                        <td className="num">—</td>
                      </tr>
                      <tr>
                        <td className="code">2131-53</td>
                        <td>WHT ค้างนำส่ง — ภ.ง.ด.53</td>
                        <td className="num">—</td>
                        <td className="num tnum">{fmt(v.certAmt)}</td>
                      </tr>
                      <tr>
                        <td className="code">2110</td>
                        <td>เจ้าหนี้</td>
                        <td className="num">—</td>
                        <td className="num tnum">{fmt(940_000 - v.certAmt)}</td>
                      </tr>
                    </>
                  ) : v.type === 'amount_mismatch' ? (
                    <>
                      <tr>
                        <td className="code">2131-53</td>
                        <td>ปรับปรุงยอด WHT (ส่วนต่าง)</td>
                        <td className="num tnum">{fmt(Math.abs(v.glAmt - v.certAmt))}</td>
                        <td className="num">—</td>
                      </tr>
                      <tr>
                        <td className="code">5xxx</td>
                        <td>ค่าใช้จ่ายอื่น (ปัดเศษ)</td>
                        <td className="num">—</td>
                        <td className="num tnum">{fmt(Math.abs(v.glAmt - v.certAmt))}</td>
                      </tr>
                    </>
                  ) : (
                    <tr>
                      <td
                        colSpan={4}
                        className="muted"
                        style={{ textAlign: 'center', padding: '12px' }}
                      >
                        ไม่ต้อง Adjusting JV · ออก Cert ย้อนหลังเท่านั้น
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose} disabled={updateM.isPending}>
            ปิด
          </button>
          {canPersist && (
            <button
              className="btn"
              onClick={() => persist('EXPLAINED')}
              disabled={updateM.isPending || !explanation}
            >
              <Icon name="check" size={14} /> Save Explanation
            </button>
          )}
          <button
            className="btn primary"
            onClick={() => persist('RESOLVED')}
            disabled={!canPersist || updateM.isPending}
          >
            <Icon name="check" size={14} />{' '}
            {v.type === 'gl_no_cert' ? 'ออก Cert ย้อนหลัง · Resolve' : 'Post & Resolve'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AnnualModal({ onClose }: { onClose: () => void }) {
  const totalBase = ANNUAL_MONTHLY.reduce((s, r) => s + r.base, 0);
  const totalWht = ANNUAL_MONTHLY.reduce((s, r) => s + r.wht, 0);
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 920,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
          maxHeight: '94vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 600,
                fontFamily: 'var(--font-display)',
              }}
            >
              ภ.ง.ด.1ก รายปี 2026 (Annual Roll-up)
            </h3>
            <div className="meta" style={{ marginTop: 2 }}>
              กำหนดยื่น 28 ก.พ. 2027 · รวบรวมจากภ.ง.ด.1 รายเดือน 12 งวด
            </div>
          </div>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div style={{ padding: '22px', overflow: 'auto', flex: 1 }}>
          <table
            className="tbl"
            style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}
          >
            <thead>
              <tr>
                <th>งวด</th>
                <th className="num">พนักงาน</th>
                <th className="num">ฐานเงินได้</th>
                <th className="num">WHT ที่หัก</th>
                <th>ยื่น ภ.ง.ด.1</th>
                <th>50 ทวิ</th>
              </tr>
            </thead>
            <tbody>
              {ANNUAL_MONTHLY.length === 0 ? (
                <tr>
                  <td colSpan={6} className="muted" style={{ padding: 32, textAlign: 'center' }}>
                    ยังไม่มีข้อมูลสำหรับช่วงเวลานี้
                  </td>
                </tr>
              ) : (
                <>
                  {ANNUAL_MONTHLY.map((r) => (
                    <tr key={r.month}>
                      <td>
                        <b>{r.month}</b>
                      </td>
                      <td className="num">{r.count}</td>
                      <td className="num tnum">{fmt(r.base)}</td>
                      <td className="num tnum">{fmt(r.wht)}</td>
                      <td>
                        <span className="badge positive">ยื่นแล้ว</span>
                      </td>
                      <td>{r.count} ฉบับ</td>
                    </tr>
                  ))}
                  <tr className="total">
                    <td>YTD</td>
                    <td className="num">{ANNUAL_MONTHLY.reduce((s, r) => s + r.count, 0)}</td>
                    <td className="num tnum">{fmt(totalBase)}</td>
                    <td className="num tnum">{fmt(totalWht)}</td>
                    <td colSpan={2} />
                  </tr>
                </>
              )}
            </tbody>
          </table>
          <div
            style={{
              marginTop: 14,
              padding: '12px 14px',
              background: '#e8eff8',
              borderRadius: 6,
              fontSize: 14,
              lineHeight: 1.55,
              display: 'flex',
              gap: 10,
            }}
          >
            <div
              style={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                background: 'var(--info)',
                color: 'white',
                display: 'grid',
                placeItems: 'center',
                fontSize: 14,
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              i
            </div>
            <div>
              ภ.ง.ด.1ก รายปี จะใช้ Sum 12 เดือน + ออก <b>หนังสือ 50ทวิ</b> ให้พนักงานทุกคนภายใน 15
              ก.พ. 2027 ตามกฎหมาย
            </div>
          </div>
        </div>
        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose}>
            ปิด
          </button>
          <button
            className="btn"
            onClick={() => {
              /* TODO(phase-3): wire */
            }}
          >
            <Icon name="download" size={14} /> Export ภ.ง.ด.1ก (XML)
          </button>
        </div>
      </div>
    </div>
  );
}
